import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Archive, CalendarDays, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import Reveal from "./Reveal";
import PremiumGate from "./PremiumGate";
import { usePosts } from "@/hooks/usePosts";
import { useLang } from "@/i18n/LangContext";
import { POSTS, ARCHIVE } from "@/data";

const Blog = () => {
  const [article, setArticle] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const { posts: apiPosts } = usePosts();
  const { t } = useLang();
  const recentPosts = apiPosts.length ? apiPosts.slice(0, 3) : POSTS;
  const archiveItems = apiPosts.length ? [...apiPosts.slice(3), ...ARCHIVE] : ARCHIVE;

  return (
    <section id="bitacora" data-testid="blog-section" className="relative py-28 lg:py-36 bg-[#111113]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-16 max-w-2xl">
          <p className="font-code text-xs tracking-[0.35em] uppercase text-[#C5A059] mb-4">
            {t("blog.overline")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] tracking-tight">
            {t("blog.headingA")} <span className="italic text-[#D4AF37]">{t("blog.headingB")}</span>
          </h2>
          <p className="mt-5 text-[#A0A0A5] leading-relaxed">
            {t("blog.intro")}
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_320px] gap-12 lg:gap-16">
          <div className="space-y-10">
            {recentPosts.map((post, i) => (
              <motion.article
                key={post.id}
                data-testid={`post-card-${post.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.85, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="group border-b border-[#F4F1EA]/10 pb-10"
              >
                <p className="font-code text-[11px] tracking-[0.3em] uppercase text-[#C5A059] flex items-center gap-2 mb-3">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {post.date}
                </p>
                <h3 className="font-display text-2xl sm:text-3xl text-[#F4F1EA] leading-tight group-hover:text-[#E5C158] transition-colors duration-300">
                  {post.title}
                </h3>
                <p className="mt-4 text-[#A0A0A5] leading-relaxed max-w-2xl">{post.excerpt}</p>
                <button
                  data-testid={`post-read-${post.id}`}
                  onClick={() => setArticle(post)}
                  className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#D4AF37] hover:gap-4 transition-all duration-300"
                >
                  {t("blog.readMore")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </motion.article>
            ))}
          </div>

          <Reveal delay={0.2} className="lg:pt-1">
            <aside className="lg:sticky lg:top-24 bg-[#141417] border border-[#F4F1EA]/10 p-7">
              <Collapsible open={archiveOpen} onOpenChange={setArchiveOpen}>
                <CollapsibleTrigger
                  data-testid="archive-toggle"
                  className="w-full flex items-center justify-between group"
                >
                  <span className="flex items-center gap-3 font-code text-xs tracking-[0.3em] uppercase text-[#C5A059]">
                    <Archive className="w-4 h-4" />
                    {t("blog.archive")}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-[#D4AF37] transition-transform duration-300 ${
                      archiveOpen ? "rotate-180" : ""
                    }`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent data-testid="archive-list" className="pt-6 space-y-1 overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                  {archiveItems.map((item) => (
                    <button
                      key={item.id}
                      data-testid={`archive-item-${item.id}`}
                      onClick={() => setArticle(item)}
                      className="w-full text-left py-3 border-b border-[#F4F1EA]/5 group"
                    >
                      <span className="block font-code text-[10px] tracking-[0.2em] text-[#8E8E93]">
                        {item.date}
                      </span>
                      <span className="block mt-1 text-sm text-[#D8D4C8] group-hover:text-[#E5C158] transition-colors duration-300">
                        {item.title}
                      </span>
                    </button>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            </aside>
          </Reveal>
        </div>
      </div>

      <Dialog open={!!article} onOpenChange={() => setArticle(null)}>
        <DialogContent
          data-testid="article-modal"
          className="bg-[#141417] border border-[#D4AF37]/25 text-[#F4F1EA] max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          {article && (
            <>
              <DialogHeader>
                <p className="font-code text-[11px] tracking-[0.3em] uppercase text-[#C5A059] mb-2">
                  {article.date} · Bitácora
                </p>
                <DialogTitle className="font-display text-2xl sm:text-3xl text-[#F4F1EA] leading-tight">
                  {article.title}
                </DialogTitle>
                <DialogDescription className="text-[#A0A0A5]">Javier Alagrande</DialogDescription>
              </DialogHeader>
              <div className="mt-5 space-y-5" data-testid="article-body">
                {(article.body || [article.excerpt]).map((p, i) => (
                  <p
                    key={i}
                    className={
                      i === 0
                        ? "font-display text-xl leading-relaxed text-[#D8D4C8]"
                        : "text-[#A0A0A5] leading-relaxed"
                    }
                  >
                    {p}
                  </p>
                ))}
              </div>
              {article.premium && <PremiumGate />}
              <div className="mt-8 h-px w-20 bg-gradient-to-r from-[#D4AF37] to-transparent" />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Blog;
